import React, { useState } from "react";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useShallow } from "zustand/react/shallow";
import { Dialog } from "@/components/main/utils/Dialog";
import { DialogActions } from "@/components/main/utils/DialogActions";
import { TextInput } from "components/common/TextInput";
import { showNotificationGlobal } from "stores/useNotificationStore";
import { resolveDatasetColor } from "@/lib/datasetColor";
import {
  SPACE_PALETTE,
  useOrgContextStore,
} from "@/components/main/stores/orgContextStore";

interface SpaceCreateDialogProps {
  open: boolean;
  onClose: () => void;
  /// Fixed by the entry point: the rail's "+" founds a root (none), a row's
  /// or the settings page's "+" creates inside that space.
  parentId?: string | null;
}

/// Swatch row (mock A): one dot per palette name; none selected = the space
/// keeps its id-derived color.
const ColorSwatches: React.FC<{
  value: string | null;
  onPick: (color: string | null) => void;
}> = ({ value, onPick }) => (
  <span className="flex shrink-0 items-center gap-1.5">
    {SPACE_PALETTE.map((name) => (
      <button
        key={name}
        aria-label={`Color ${name}`}
        aria-pressed={value === name}
        onClick={() => onPick(value === name ? null : name)}
        className={`h-4 w-4 rounded-app border ${
          value === name
            ? "border-[var(--color-fg)]"
            : "border-transparent hover:border-border"
        }`}
        style={{ backgroundColor: resolveDatasetColor(name) }}
      />
    ))}
  </span>
);

type RootKind = "restricted" | "sealed";

/// D50: WHERE decides WHAT, and the entry point decided where. Inside a space
/// the new space is Open — it inherits, and inheriting is the only thing that
/// nests. At the top level nothing inherits, so the one choice left is how
/// closed the space is.
export const SpaceCreateDialog: React.FC<SpaceCreateDialogProps> = ({
  open,
  onClose,
  parentId = null,
}) => {
  const [name, setName] = useState("");
  const [color, setColor] = useState<string | null>(null);
  const [rootKind, setRootKind] = useState<RootKind>("restricted");
  const [isCreating, setIsCreating] = useState(false);
  const { createSpace, spaceById } = useOrgContextStore(
    useShallow((s) => ({
      createSpace: s.createSpace,
      spaceById: s.spaceById,
    })),
  );

  const parent = spaceById(parentId);
  const sealed = !parent && rootKind === "sealed";
  // A Sealed parent walls its Open children (D40), whatever they are called.
  const walled = parent?.classification === "sealed";

  const close = () => {
    setName("");
    setColor(null);
    setRootKind("restricted");
    onClose();
  };

  const submit = async () => {
    if (isCreating) return;
    setIsCreating(true);
    try {
      // Nobody named ⇒ the door makes the founder the first manager, sealed
      // included; further managers are granted in the space's settings.
      const created = await createSpace(name.trim(), {
        parentSpaceId: parent?.space_id,
        color: color ?? undefined,
        classification: parent ? "open" : rootKind,
      });
      if (created) {
        showNotificationGlobal(`Space "${created.name}" created`, "success");
        close();
      } else {
        showNotificationGlobal("Failed to create space", "error");
      }
    } catch (e) {
      showNotificationGlobal(
        e instanceof Error ? e.message : "Failed to create space",
        "error",
      );
    } finally {
      setIsCreating(false);
    }
  };

  const canSubmit = name.trim().length >= 2;

  const card = (
    active: boolean,
    onPick: () => void,
    title: React.ReactNode,
    detail: string,
  ) => (
    <button
      role="radio"
      aria-checked={active}
      onClick={onPick}
      className={`flex w-full flex-col gap-0.5 rounded-app border px-3 py-2 text-left ${
        active
          ? "border-[var(--color-accent)] bg-[var(--color-accent-subtle)]"
          : "border-border hover:bg-list-hover"
      }`}
    >
      <span className="flex items-center gap-1.5 text-fg-medium">{title}</span>
      <span className="text-fg-muted-regular-small">{detail}</span>
    </button>
  );

  return (
    <Dialog
      open={open}
      title={parent ? `New space inside ${parent.name}` : "New space"}
      onClose={close}
    >
      <div className="flex flex-col gap-4">
        <label className="flex flex-col gap-1">
          <span className="text-fg-muted-regular-small">Name</span>
          <span className="flex items-center gap-2">
            <TextInput
              value={name}
              onChange={setName}
              placeholder="e.g. Client Work"
              autoFocus
              maxLength={80}
              ariaLabel="Space name"
              onKeyDown={(e) => {
                if (e.key === "Enter" && canSubmit) void submit();
              }}
            />
            <ColorSwatches value={color} onPick={setColor} />
          </span>
        </label>

        {parent ? (
          walled ? (
            <span
              data-testid="parent-cap"
              className="rounded-app border border-warning px-3 py-2 text-fg-muted-regular-small"
            >
              Inside <b className="text-fg-medium">{parent.name}</b>, which is
              sealed. This space is <b className="text-fg-medium">walled</b>:
              data moves only inside {parent.name}, with no export and no
              sharing out.
            </span>
          ) : (
            <span className="text-fg-muted-regular-small">
              {`Inherits ${parent.name}'s access — whoever can see ${parent.name} sees this.`}
            </span>
          )
        ) : (
          <div role="radiogroup" className="flex flex-col gap-1.5">
            {card(
              rootKind === "restricted",
              () => setRootKind("restricted"),
              "Restricted",
              "inherits nothing · every grant named · named to others only through linked spaces",
            )}
            {card(
              sealed,
              () => setRootKind("sealed"),
              <>
                Sealed
                <LockOutlinedIcon sx={{ fontSize: 12 }} />
              </>,
              "invisible without a grant · nothing leaves: no export, no sharing, no MCP",
            )}
          </div>
        )}

        <span className="flex items-start gap-1.5 text-fg-muted-regular-small">
          <InfoOutlinedIcon sx={{ fontSize: 12 }} className="mt-px shrink-0" />
          {parent
            ? "Placement is permanent: to move the contents elsewhere later, create a new space and move them into it."
            : "Nothing is inherited at the top level — you grant access by name. Placement is permanent: to change it, create a new space and move the contents into it."}
        </span>
      </div>
      <DialogActions
        actions={[
          { label: "Cancel", variant: "secondary", onClick: close },
          {
            label: sealed ? "Create sealed space" : "Create space",
            variant: "primary",
            onClick: () => void submit(),
            disabled: !canSubmit,
            loading: isCreating,
          },
        ]}
      />
    </Dialog>
  );
};
